import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { elliottTokens as t } from './tokens';

const SEP = '\n\n';

type StreamingTextProps = {
  paragraphs: string[];
  sx?: SxProps<Theme>;
  charsPerTick?: number;
  tickMs?: number;
  enabled?: boolean; // when false, renders the full text immediately (no animation)
  onDone?: () => void;
};

// Types Elliott's copy out live, paragraph by paragraph, with a blinking caret while streaming.
// Ported from the Elliott lesson redesign so the typing feel matches.
const StreamingText: React.FC<StreamingTextProps> = ({
  paragraphs,
  sx,
  charsPerTick = 3,
  tickMs = 16,
  enabled = true,
  onDone
}) => {
  const full = paragraphs.join(SEP);
  const [count, setCount] = useState(enabled ? 0 : full.length);
  const doneRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      setCount(full.length);
      return;
    }
    doneRef.current = false;
    setCount(0);
    let i = 0;
    const id = setInterval(() => {
      i = Math.min(i + charsPerTick, full.length);
      setCount(i);
      if (i >= full.length) {
        clearInterval(id);
        if (!doneRef.current) {
          doneRef.current = true;
          onDone?.();
        }
      }
    }, tickMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [full, enabled]);

  const parts = full.slice(0, count).split(SEP);
  const streaming = count < full.length;

  return (
    <>
      {parts.map((p, idx) => (
        <Typography key={idx} sx={sx}>
          {p}
          {streaming && idx === parts.length - 1 && (
            <Box
              component="span"
              sx={{
                ml: '2px',
                color: t.brandLight,
                animation: 'addProgCaret 1s steps(1) infinite',
                '@keyframes addProgCaret': {
                  '0%, 50%': { opacity: 1 },
                  '50.01%, 100%': { opacity: 0 }
                }
              }}
            >
              ▍
            </Box>
          )}
        </Typography>
      ))}
    </>
  );
};

export default StreamingText;
